import { useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import type {
  DialogueRoleplayInput,
  DialogueAutoInput,
  DialogueTurn,
  DialogueSimulationResult,
  CharacterRelationship,
  ContinueDialogueInput,
  CompressionResult
} from '@creanizer/types'
import { apiClient, ApiError } from '../services/api'
import { useDialogueSimulatorStore } from '../stores/dialogueSimulatorStore'
import { useSettingsStore } from '../stores/settingsStore'
import { toast } from 'sonner'
import { useGeneratorModel } from '../features/generators/components/GeneratorModelSelector'

export function useDialogueSimulator() {
  const dialogueStore = useDialogueSimulatorStore()
  const settingsStore = useSettingsStore()
  const modelId = useGeneratorModel('dialogue')
  
  // エラーハンドリング
  const handleApiError = useCallback((error: any) => {
    if (error instanceof ApiError) {
      switch (error.status) {
        case 429:
          toast.error('レート制限に達しました。別のAPIキーを使用してください。')
          // 次の利用可能なキーを取得して設定
          const nextKey = settingsStore.getNextAvailableApiKey()
          if (nextKey) {
            settingsStore.setActiveApiKey(nextKey.id)
          }
          break
        case 400:
          toast.error('入力内容に問題があります。設定を確認してください。')
          break
        case 500:
          toast.error('サーバーエラーが発生しました。しばらく待ってから再試行してください。')
          break
        default:
          toast.error(`APIエラー: ${error.message}`)
      }
    } else {
      console.error('Unexpected error:', error)
      toast.error('予期しないエラーが発生しました。')
    }
  }, [settingsStore])
  
  // ロールプレイ生成ミューテーション
  const generateRoleplayMutation = useMutation({
    mutationFn: async (input: DialogueRoleplayInput) => {
      console.log('Sending roleplay request with input:', input)
      console.log('Character data details:', input.participants.map(p => ({
        name: p.characterName,
        hasData: !!p.characterData,
        speechStyle: p.characterData?.speechStyle,
        personality: p.characterData?.personality
      })))
      const response = await apiClient.dialogueGenerate({ ...input, modelId })
      console.log('API response:', response)
      console.log('API response.result:', response.result)

      // 動作描写・内心描写のフィルタリング
      const filteredResult = response.result as DialogueSimulationResult
      if (input.options?.includeActions === false) {
        filteredResult.dialogues = filteredResult.dialogues.map(d => ({
          ...d,
          action: undefined
        }))
      }
      if (input.options?.includeThoughts === false) {
        filteredResult.dialogues = filteredResult.dialogues.map(d => ({
          ...d,
          thought: undefined
        }))
      }

      return { result: filteredResult, input }
    },
    onSuccess: ({ result, input }) => {
      console.log('Roleplay mutation success, result:', result)
      const currentSimulation = dialogueStore.activeSimulation

      // 初回（activeSimulationがない）の場合：新規シミュレーション作成
      if (!currentSimulation) {
        console.log('Creating new simulation')
        dialogueStore.setSimulation(result)

        // 最初のメッセージでタイトルを自動生成
        if (result.dialogues.length > 0) {
          const firstMessage = result.dialogues[0].content
          setTimeout(() => {
            const simulationId = dialogueStore.activeSimulation?.id
            console.log('Updating title for simulation:', simulationId)
            if (simulationId) {
              dialogueStore.updateSimulationTitle(simulationId, firstMessage)
            }
          }, 50)
        }
      } else {
        // 2回目以降：既存シミュレーションに追加
        console.log('Adding to existing simulation')

        // result.dialoguesには既にプレイヤーメッセージ + NPCの応答が含まれているので、そのまま追加
        for (const turn of result.dialogues) {
          dialogueStore.addDialogueTurn(turn)
        }
      }

      console.log('After processing, activeSimulation:', dialogueStore.activeSimulation)
    },
    onError: handleApiError
  })
  
  // 自動生成ミューテーション
  const generateAutoMutation = useMutation({
    mutationFn: async (input: DialogueAutoInput) => {
      console.log('Sending auto request with input:', input)
      console.log('Character data details:', input.participants.map(p => ({
        name: p.characterName,
        hasData: !!p.characterData,
        speechStyle: p.characterData?.speechStyle,
        personality: p.characterData?.personality
      })))
      const response = await apiClient.dialogueGenerate({ ...input, modelId })
      console.log('API response:', response)
      console.log('API response.result:', response.result)
      
      // 動作描写・内心描写のフィルタリング
      const filteredResult = response.result as DialogueSimulationResult
      if (input.options?.includeActions === false) {
        filteredResult.dialogues = filteredResult.dialogues.map(d => ({
          ...d,
          action: undefined
        }))
      }
      if (input.options?.includeThoughts === false) {
        filteredResult.dialogues = filteredResult.dialogues.map(d => ({
          ...d,
          thought: undefined
        }))
      }
      
      return filteredResult
    },
    onSuccess: (result) => {
      console.log('Auto mutation success, result:', result)
      dialogueStore.setSimulation(result)
      console.log('After setSimulation, activeSimulation:', dialogueStore.activeSimulation)
      
      // 最初のメッセージでタイトルを自動生成
      if (result.dialogues.length > 0) {
        const firstMessage = result.dialogues[0].content
        // setSimulationの後すぐにactiveSimulationを取得
        setTimeout(() => {
          const simulationId = dialogueStore.activeSimulation?.id
          console.log('Updating title for simulation:', simulationId)
          if (simulationId) {
            dialogueStore.updateSimulationTitle(simulationId, firstMessage)
          }
        }, 50)
      }
    },
    onError: handleApiError
  })
  
  // 続き生成ミューテーション
  const continueMutation = useMutation({
    mutationFn: async ({ simulationId, turns = 5, options, context, useGemini3Mode, useGemini3FlashMode }: { simulationId: string; turns?: number; options?: any; context?: any; useGemini3Mode?: boolean; useGemini3FlashMode?: boolean }) => {
      const simulation = dialogueStore.getSimulationById(simulationId)
      if (!simulation) throw new Error('Simulation not found')

      // sceneTransitionをoptionsから取り出してcontinuationPromptとして設定
      const { sceneTransition, ...restOptions } = options || {}

      const input: ContinueDialogueInput = {
        simulationId,
        existingDialogues: simulation.result.dialogues,
        participants: dialogueStore.currentParticipants,  // 現在の参加者を使用
        sceneSettings: dialogueStore.currentScene,  // 現在のシーン設定を使用（変更可能）
        relationships: dialogueStore.currentRelationships,  // 現在の関係性を使用
        turnsToGenerate: turns,
        continuationPrompt: sceneTransition,  // シーン遷移の説明を渡す
        context,  // コンテキストを追加
        useGemini3Mode,  // Gemini 3.0モード
        useGemini3FlashMode,  // Gemini 3.0 Flashモード
        options: restOptions  // sceneTransition以外のオプションを渡す
      }
      
      console.log('Continue dialogue with participants:', input.participants.map(p => ({
        name: p.characterName,
        hasData: !!p.characterData,
        speechStyle: p.characterData?.speechStyle
      })))
      
      const response = await apiClient.dialogueContinue({ ...input, modelId })
      let newTurns = response.result as DialogueTurn[]
      
      // 動作描写・内心描写のフィルタリング
      if (options?.includeActions === false) {
        newTurns = newTurns.map(turn => ({
          ...turn,
          action: undefined
        }))
      }
      if (options?.includeThoughts === false) {
        newTurns = newTurns.map(turn => ({
          ...turn,
          thought: undefined
        }))
      }
      
      return newTurns
    },
    onSuccess: (newTurns) => {
      for (const turn of newTurns) {
        dialogueStore.addDialogueTurn(turn)
      }
      if (dialogueStore.activeSimulation) {
        dialogueStore.incrementContinuationCount(dialogueStore.activeSimulation.id)
      }
    },
    onError: handleApiError
  })
  
  // 関係性分析ミューテーション
  const analyzeRelationshipMutation = useMutation({
    mutationFn: async ({ char1, char2 }: { char1: any; char2: any }) => {
      const response = await apiClient.dialogueAnalyzeRelationship({
        character1: char1,
        character2: char2
      })
      return response.result as CharacterRelationship
    },
    onSuccess: (relationship) => {
      dialogueStore.setRelationship(relationship)
    },
    onError: handleApiError
  })
  
  // 履歴圧縮ミューテーション
  const compressHistoryMutation = useMutation({
    mutationFn: async ({ simulationId, keepRecentCount = 10 }: { simulationId: string; keepRecentCount?: number }) => {
      const simulation = dialogueStore.getSimulationById(simulationId)
      if (!simulation) throw new Error('Simulation not found')
      
      const response = await apiClient.dialogueCompress({
        dialogues: simulation.result.dialogues,
        keepRecentCount,
        preserveKeyMoments: true,
        participants: simulation.result.participants,
        sceneSettings: simulation.result.sceneSettings,
        modelId
      })
      return response.result as CompressionResult
    },
    onSuccess: (result, variables) => {
      // 圧縮された履歴でシミュレーションを更新
      const simulation = dialogueStore.getSimulationById(variables.simulationId)
      if (simulation && result.status === 'success') {
        // 既存のシミュレーションを更新（新しいシミュレーションを作成しない）
        dialogueStore.updateSimulation(variables.simulationId, {
          dialogues: result.compressedDialogues,
          metadata: {
            ...simulation.result.metadata,
            compressionSummary: result.summary,
            lastCompressed: new Date().toISOString()
          }
        })
        toast.success(`履歴を圧縮しました（${result.originalMessageCount}→${result.compressedMessageCount}メッセージ）`)
      } else if (result.status === 'no_compression_needed') {
        toast.info('圧縮する履歴がありません')
      }
    },
    onError: handleApiError
  })
  
  // 最後のターンを再生成
  const regenerateLastTurn = useCallback(async (simulationId: string) => {
    const simulation = dialogueStore.getSimulationById(simulationId)
    if (!simulation) throw new Error('Simulation not found')
    
    // 最後のAI生成ターンを削除
    const lastAiTurn = [...simulation.result.dialogues]
      .reverse()
      .find(t => t.metadata?.generatedBy === 'ai')
    
    if (!lastAiTurn) throw new Error('No AI turn to regenerate')
    
    dialogueStore.deleteDialogueTurn(simulationId, lastAiTurn.id)
    
    // 再生成
    if (simulation.result.mode === 'roleplay') {
      // ロールプレイモードの場合、最後のユーザー入力から再生成
      const lastUserTurn = [...simulation.result.dialogues]
        .reverse()
        .find(t => t.metadata?.generatedBy === 'user')
      
      if (!lastUserTurn) throw new Error('No user turn found')
      
      return generateRoleplayMutation.mutateAsync({
        mode: 'roleplay',
        participants: simulation.result.participants,
        playerCharacterId: lastUserTurn.characterId,
        playerMessage: lastUserTurn.content,
        sceneSettings: simulation.result.sceneSettings,
        relationships: simulation.result.relationships,
        dialogueHistory: simulation.result.dialogues.slice(0, -1),
        options: {
          responseLength: simulation.result.options?.responseLength || 'medium',
          includeActions: simulation.result.options?.includeActions ?? true,
          includeThoughts: simulation.result.options?.includeThoughts ?? false,
          emotionalIntensity: simulation.result.options?.emotionalIntensity || 5
        }  // オプションを引き継ぐ（デフォルト値付き）
      })
    } else {
      // 自動生成モードの場合、最後の1ターンだけ再生成
      return continueMutation.mutateAsync({
        simulationId,
        turns: 1,
        options: {
          responseLength: simulation.result.options?.responseLength || 'medium',
          includeActions: simulation.result.options?.includeActions ?? true,
          includeThoughts: simulation.result.options?.includeThoughts ?? false,
          emotionalIntensity: simulation.result.options?.emotionalIntensity || 5
        }  // オプションを引き継ぐ（デフォルト値付き）
      })
    }
  }, [dialogueStore, generateRoleplayMutation, continueMutation])
  
  return {
    // 生成関数
    generateRoleplay: generateRoleplayMutation.mutateAsync,
    generateAuto: generateAutoMutation.mutateAsync,
    continueDialogue: continueMutation.mutateAsync,
    regenerateLastTurn,
    analyzeRelationship: analyzeRelationshipMutation.mutateAsync,
    compressHistory: compressHistoryMutation.mutateAsync,
    
    // 編集関数（ストアから直接）
    editDialogueTurn: dialogueStore.editDialogueTurn,
    deleteDialogueTurn: dialogueStore.deleteDialogueTurn,
    deleteMessagesAfter: dialogueStore.deleteMessagesAfter,
    
    // ローディング状態
    isGenerating: generateRoleplayMutation.isPending || generateAutoMutation.isPending,
    isContinuing: continueMutation.isPending,
    isAnalyzing: analyzeRelationshipMutation.isPending,
    isCompressing: compressHistoryMutation.isPending,
    
    // エラー状態
    error: generateRoleplayMutation.error || generateAutoMutation.error || continueMutation.error
  }
}
